import "@/app/sentry";
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "@/lib/theme";
import { loadConfig } from "@/app/config";
import { createAppRouter } from "@/app/router";
import { AppErrorFallback } from "@/components/AppErrorFallback";
import { isAuthError } from "@/lib/errors";
import "@/index.css";

/**
 * QueryClient mirrors the Next.js `AppWrapper` config: no refetch on focus,
 * 5-minute stale time, no retry on auth errors (otherwise one retry), and no
 * mutation retries.
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isAuthError(error)) return false;
          return failureCount < 1;
        },
        staleTime: 5 * 60 * 1000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

async function bootstrap() {
  await loadConfig();

  const queryClient = makeQueryClient();
  const router = createAppRouter(queryClient);

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("#root element not found");

  createRoot(rootEl).render(
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={({ resetError, eventId }) => (
          <AppErrorFallback resetError={resetError} eventId={eventId} />
        )}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <RouterProvider router={router} />
          </ThemeProvider>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
}

bootstrap();
